import { Button } from "@/shared/components/ui/button";
import IconButton from "@/shared/components/ui/icon-button";
import { Bell, PanelLeft, FileText } from "lucide-react";
import { Card } from "@/shared/components/ui/card";
import { useLocation, useNavigate } from "react-router-dom";
import { ROUTES } from "@/core/router/routes";
import { useSelector } from "@/core/redux/store";
import { getDashboardLink } from "@/shared/utils/link";
import { useState, useEffect } from "react";
import { IInviteListResponse } from "@/shared/services/interface/responses/invite-response";
import { specificationService } from "@/shared/services/api/specification-service";
import ProductCategory from "../category-sidebar/product-category/product-category";
import image1 from "/images/image1.png";
import image2 from "/images/image2.png";
import ProductCategoryReadonly from "../category-sidebar/product-category/product-category-readonly";
import UploadCreditCardPopover from "@/features/dashboard/pages/setting/pages/plans-and-billings/components/upload-credit/upload-credit-card-popover";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/shared/components/ui/breadcrumb";
import { Separator } from "@/shared/components/ui/separator";
import UserDropdown from "@/shared/components/user-dropdown/user-dropdown";
/* istanbul ignore file */
import { useTranslation } from "react-i18next";

const user = [
  {
    name: "Amelia",
    email: "amelia@example.com",
    avatar: image1,
  },
  {
    name: "John Doe",
    email: "john.doe@example.com",
    avatar: image2,
  },
  {
    name: "Adem",
    email: "adem@example.com",
    avatar: null,
  },
];

export default function Header({
  hideRing = true,
  card = false,
  compareButton = false,
  userCircle = false,
  isSidebarCollapsed = false,
  toggleSidebar,
}: {
  hideRing?: boolean;
  card?: boolean;
  compareButton?: boolean;
  userCircle?: boolean;
  isSidebarCollapsed?: boolean;
  toggleSidebar?: () => void;
}) {
  const [inviteList] = useState<IInviteListResponse[]>([]);

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isCreateSpecRoute = pathname === "/dashboard/specification/create";
  const isProductDiscoveryRoute =
    pathname === "/dashboard/specification/product-discovery";
  const isProductAnswersRoute =
    pathname === "/dashboard/specification/product-answers";
  const specification = useSelector(
    (state) => state.specification.specification
  );
  const userDetail = useSelector((state) => state.user.me);
  const company = userDetail?.company;

  const handleSkip = async () => {
    if (specification.specification.id && company?.id) {
      try {
        const response = await specificationService.skipSpecification(
          company.id,
          specification.specification.id
        );
        if (response.success) {
          navigate(
            getDashboardLink(
              `${ROUTES.Dashboard.Specification.CompareDetail}?specificationId=${specification.specification.id}&companyId=${company.id}`
            )
          );
        }
      } catch (error) {
        console.error("Skip failed:", error);
      }
    }
  };

  const Container = ({ children }: { children: React.ReactNode }) => {
    return card ? (
      <Card className="flex items-center py-2 px-3">{children}</Card>
    ) : (
      <div className="flex items-center">{children}</div>
    );
  };

  const acceptedUsers = inviteList.filter((item) => item.status === 1);
  const containerWidth =
    acceptedUsers.length > 0 ? Math.min(acceptedUsers.length, 3) * 23 + 40 : 0;

  const specificationData = useSelector(
    (state) => state.specification.specification
  );
  const supplierHashResponse = useSelector(
    (state) => state.productAnswer.supplierHashResponse
  );
  const { t } = useTranslation();

  // Get product or service name for supplier answer header
  const productOrServiceName = supplierHashResponse?.product?.name || supplierHashResponse?.service?.name || "";

  return (
    <header className="rounded-t-xl flex justify-between items-center sticky top-0 shrink-0 gap-2 border-b h-[50px] mx-4 z-[1000] bg-white">
      <div className="w-max-w flex items-center">
        {/* Sidebar Toggle Button */}
        <button
          onClick={toggleSidebar}
          className="flex h-7 w-7 items-center justify-center rounded-md transition-colors hover:bg-accent hover:text-accent-foreground -ml-1"
        >
          <PanelLeft className="h-4 w-4" />
          <span className="sr-only">Toggle Sidebar</span>
        </button>

        <Separator orientation="vertical" className="mr-2 h-4" />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbPage className="font-600 text-[#09090B] mt-[3px] cursor-default">
                {specificationData.specification.name}
              </BreadcrumbPage>
            </BreadcrumbItem>
            {isProductAnswersRoute && productOrServiceName && (
              <>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage className="font-600 text-[#09090B] mt-[3px] cursor-default flex items-center gap-1.5">
                    <span className="w-3 h-3 bg-zinc-900 rounded-sm inline-block" />
                    {productOrServiceName}
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </>
            )}
          </BreadcrumbList>
        </Breadcrumb>
      </div>
      <div className="w-max-w">
        <div className="w-full flex justify-end items-center cursor-pointer">
          {isCreateSpecRoute && <ProductCategory />}
          {isProductDiscoveryRoute && <ProductCategoryReadonly />}
          {isProductAnswersRoute && (
            <Button
              variant="ghost"
              className="text-sm font-500 text-zinc-600 flex items-center gap-1.5 h-8 px-3"
            >
              <FileText width={14} height={14} />
              {t("answer-with-document")}
            </Button>
          )}
          {!isProductAnswersRoute && (
            <>
              {!hideRing && (
                <IconButton border={false} width={32} height={32}>
                  <Bell
                    width={16}
                    height={16}
                    className="hover:!bg-accent"
                    color="#000"
                  />
                </IconButton>
              )}
              <div className="flex items-center gap-1">
                <UploadCreditCardPopover />
                <UserDropdown />
              </div>
            </>
          )}

          <Container>
            {compareButton && (
              <>
                <Button
                  className="font-sans font-semibold max-w-max text-sm leading-6 px-2 py-1.5"
                  style={{
                    height: "32px",
                    minWidth: "64px",
                    backgroundColor: "rgba(24, 24, 27, 1)",
                    color: "#FAFAFA",
                  }}
                  onClick={handleSkip}
                >
                  Skip
                </Button>
              </>
            )}
          </Container>
        </div>
      </div>
    </header>
  );
}
